import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import CssTransition from '../../components/Transition/CssTransition';
import { connect } from 'react-redux';

import {
    RepOverlayContainer as VolunteerOverlayContainer,
    RepAction,
    CloseButton,
    OverlayBackground
} from '../../components/Overlay/OverlayComponents';

import { SkillsIcon, LocationIcon } from '../../components/Volunteer/Shared';
import {
    toggleOverlayVolunteerVisibility,
    onApplyForVolunteerOpportunity
} from '../Volunteer/actions';

import { buildMapsUrl, buildDateRange, buildTimeRange } from '../Volunteer/helpers';
import { copyFor } from '../../copy/copy';
import { isPortrait } from '../../utils/size';
import ThemeContext from '../../utils/theming';
import { styledScrollbar } from '../../components/Shared/mixins';

const ApplyButton = styled(RepAction)`
    height: 45px;
    font-size: 16px;
`;

const VolunteerInfo = styled.div`
    width: 100%;
    height: 240px;
    ${() => isPortrait() && `height: calc(100% - 70px); width: 100%;`};
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const ScrollContainer = styled.div`
    flex: 1;
    overflow-x: hidden;
    overflow-y: scroll;
    padding-right: 10px;
    flex-direction: column;
    ${({ theme }) => styledScrollbar(theme.colors?.primary)};
`;

const Title = styled.h3`
    font-family: Averta;
    font-size: 24px;
    font-weight: 300;
    line-height: 1.25;
    margin: 0;
    margin-bottom: 24px;
`;
const Subtitle = styled.h4`
    font-family: Lato;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.29;
    margin: 0;
    margin-bottom: 4px;
`;
const Description = styled.p`
    font-family: Lato;
    font-size: 12px;
    line-height: 1.33;
`;
const Detail = styled(Description)`
    display: flex;
    align-items: flex-start;
    margin-bottom: 3px;
    svg {
        width: 10px;
        min-width: 10px;
        margin-top: 3px;
    }
    span {
        margin-left: 0.5em;
    }
    a {
        color: ${({ theme }) => theme.colors?.primary};
        text-decoration: none;
    }
`;

const OverlayVolunteer = props => {
    const theme = useContext(ThemeContext);
    return (
        <CssTransition in={props.isVisible}>
            <OverlayBackground
                isVisible={props.isVisible}
                onClick={() => props.toggleOverlayVolunteerVisibility(false)}
            >
                <CssTransition in={props.isVisible}>
                    <VolunteerOverlayContainer
                        isVisible={props.isVisible}
                        onClick={proxy => proxy.stopPropagation()}
                        transitionType={props.transitionType}
                    >
                        <CloseButton
                            style={{ float: 'right' }}
                            onClick={() => props.toggleOverlayVolunteerVisibility(false)}
                        >
                            +
                        </CloseButton>
                        <VolunteerInfo>
                            {!!props.opportunity && (
                                <>
                                    <Title>{props.opportunity.title}</Title>
                                    <ScrollContainer theme={theme}>
                                        <Subtitle>
                                            {buildDateRange(
                                                (props.opportunity || {}).schedule
                                            )}
                                            {!!buildTimeRange(
                                                (props.opportunity || {}).schedule
                                            )
                                                ? `, ${buildTimeRange(
                                                      (props.opportunity || {}).schedule
                                                  )}`
                                                : ``}
                                        </Subtitle>
                                        {(!!props.opportunity.address ||
                                            !!props.opportunity.virtual) && (
                                            <Detail theme={theme}>
                                                <LocationIcon />
                                                {props.opportunity.virtual && (
                                                    <span>
                                                        {copyFor(
                                                            'opportunity',
                                                            'virtual',
                                                            'volunteer'
                                                        )}
                                                    </span>
                                                )}
                                                {props.opportunity.address && (
                                                    <>
                                                        <span>
                                                            {props.opportunity.address
                                                                .street1 || ''}
                                                            {`${props.opportunity.address
                                                                .street2 || ''} ${
                                                                props.opportunity.address
                                                                    .city
                                                                    ? props.opportunity
                                                                          .address.city +
                                                                      ','
                                                                    : ''
                                                            } ${
                                                                props.opportunity.address
                                                                    .state
                                                            } ${props.opportunity.address
                                                                .zipCode || ''}`}{' '}
                                                            <a
                                                                className={'mapLink'}
                                                                href={buildMapsUrl(
                                                                    props.opportunity
                                                                        .address.street1,
                                                                    props.opportunity
                                                                        .address.street2,
                                                                    props.opportunity
                                                                        .address.city,
                                                                    props.opportunity
                                                                        .address.state,
                                                                    props.opportunity
                                                                        .address.zipCode
                                                                )}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                &nbsp; &nbsp;
                                                                {copyFor(
                                                                    'opportunity',
                                                                    'map',
                                                                    'volunteer'
                                                                )}
                                                            </a>
                                                        </span>
                                                    </>
                                                )}
                                            </Detail>
                                        )}
                                        {props.opportunity.skillsList.length > 0 && (
                                            <Detail>
                                                <SkillsIcon />
                                                <span>
                                                    {' '}
                                                    {props.opportunity.skillsList
                                                        .slice(0, 3)
                                                        .map(
                                                            (s, i) => (i ? ', ' : '') + s
                                                        )}
                                                </span>
                                            </Detail>
                                        )}
                                        <Subtitle style={{ marginTop: '24px' }}>
                                            {`About ${
                                                (props.opportunity.parentOrg || {}).name
                                            }`}
                                        </Subtitle>
                                        <Description>
                                            {props.opportunity.description}
                                        </Description>
                                    </ScrollContainer>
                                </>
                            )}
                        </VolunteerInfo>
                        <ApplyButton
                            theme={theme}
                            onClick={() => props.onApplyForVolunteerOpportunity()}
                        >
                            <span>{'APPLY'}</span>
                        </ApplyButton>
                    </VolunteerOverlayContainer>
                </CssTransition>
            </OverlayBackground>
        </CssTransition>
    );
};
OverlayVolunteer.propTypes = {
    onApplyForVolunteerOpportunity: PropTypes.func.isRequired,
    isVisible: PropTypes.bool,
    toggleOverlayVolunteerVisibility: PropTypes.func.isRequired,
    opportunity: PropTypes.object,
    transitionType: PropTypes.string
};

function arePropsEqual(prevProps, nextProps) {
    if (
        prevProps.isVisible !== nextProps.isVisible ||
        prevProps.opportunity !== nextProps.opportunity
    ) {
        return false;
    } else {
        return true;
    }
}

export default connect(
    state => {
        return {
            opportunity: state.volunteer.selectedVolunteerOpportunity,
            isVisible: state.volunteer.isOverlayVolunteerVisible,
            isMobileResolution: state.widget.isMobileResolution
        };
    },
    {
        toggleOverlayVolunteerVisibility,
        onApplyForVolunteerOpportunity
    }
)(memo(OverlayVolunteer, arePropsEqual));
